import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import {
  useApplicationServices,
  ApplicationService,
  ApplicationServiceRequest,
} from "../../../hooks/useApplicationServices";
import { useError } from "../../../contexts/ErrorContext";
import CommonModal from "../../../components/common/CommonModal";
import EnhancedTable from "../../../components/common/EnhancedTable";
import ActionButtons from "../../../components/common/ActionButtons";
import ServiceFormModal from "../../../components/Modals/ServiceFormModal";

const ServiceManagement: React.FC = () => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useError();
  const { servicesQuery, createServiceMutation, updateServiceMutation, deleteServiceMutation } =
    useApplicationServices();

  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentService, setCurrentService] = useState<ApplicationService | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<number | null>(null);

  const handleOpenAddModal = () => {
    setIsEdit(false);
    setCurrentService(undefined);
    setModalOpen(true);
  };

  const handleOpenEditModal = (service: ApplicationService) => {
    setIsEdit(true);
    setCurrentService(service);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentService(undefined);
  };

  const handleSubmit = (formData: ApplicationServiceRequest & { id?: number }) => {
    const requestBody: ApplicationServiceRequest = {
      code: formData.code,
      name: formData.name,
      applicationServiceParameter: formData.applicationServiceParameter ?? [],
      active: formData.active,
    };

    if (isEdit && formData.id != null) {
      updateServiceMutation.mutate(
        { id: formData.id, data: requestBody },
        {
          onSuccess: () => {
            showSuccess(t("service_updated_successfully"));
            handleCloseModal();
          },
          onError: (error: unknown) => {
            const err = error as { response?: { data?: { message?: string } }; message?: string };
            showError(err?.response?.data?.message || err?.message || t("error_updating_service"));
          },
        }
      );
    } else {
      createServiceMutation.mutate(requestBody, {
        onSuccess: () => {
          showSuccess(t("service_added_successfully"));
          handleCloseModal();
        },
        onError: (error: unknown) => {
          const err = error as { response?: { data?: { message?: string } }; message?: string };
          showError(err?.response?.data?.message || err?.message || t("error_adding_service"));
        },
      });
    }
  };

  const handleConfirmDelete = () => {
    if (serviceToDelete == null) return;
    deleteServiceMutation.mutate(serviceToDelete, {
      onSuccess: () => {
        showSuccess(t("service_deleted_successfully"));
        setDeleteDialogOpen(false);
        setServiceToDelete(null);
      },
      onError: (error: unknown) => {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        showError(err?.response?.data?.message || err?.message || t("error_deleting_service"));
      },
    });
  };

  const columns = [
    { id: "code", label: t("service_code"), minWidth: 120 },
    { id: "name", label: t("service_name"), minWidth: 180 },
    {
      id: "active",
      label: t("status"),
      minWidth: 100,
      align: "center" as const,
      format: (value: boolean) => (
        <Chip
          label={value ? t("active") : t("inactive")}
          color={value ? "success" : "default"}
          size="small"
        />
      ),
    },
  ];

  const formattedData = (servicesQuery.data || []).map((row) => ({
    ...row,
    code: row.code || "-",
    name: row.name || "-",
    active: row.active ?? false,
  }));

  const isSubmitting = createServiceMutation.isLoading || updateServiceMutation.isLoading;

  return (
    <>
      <Paper sx={{ p: 2, maxWidth: 1500, mx: "auto", mt: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            {t("service_management")}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
            onClick={handleOpenAddModal}
          >
            {t("add_service")}
          </Button>
        </Box>

        {servicesQuery.isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : servicesQuery.error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {servicesQuery.error.message || t("error_loading_services")}
          </Alert>
        ) : (
          <EnhancedTable
            columns={columns}
            data={formattedData}
            searchFields={["code", "name"]}
            filterFields={[]}
            actions={(row: ApplicationService) => (
              <ActionButtons
                onEdit={() => handleOpenEditModal(row)}
                onDelete={() => row.id != null && (setServiceToDelete(row.id), setDeleteDialogOpen(true))}
                editTooltip={t("edit")}
                deleteTooltip={t("delete")}
              />
            )}
            cardProps={{
              elevation: 0,
              sx: { border: "none", boxShadow: "none", backgroundColor: "transparent" },
            }}
          />
        )}
      </Paper>

      <ServiceFormModal
        open={modalOpen}
        handleClose={handleCloseModal}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isEdit={isEdit}
        initialData={currentService}
      />

      <CommonModal
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={t("confirm_delete")}
        maxWidth="xs"
        customActions={
          <>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              variant="outlined"
              disabled={deleteServiceMutation.isLoading}
              sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
            >
              {t("cancel")}
            </Button>
            <Button onClick={handleConfirmDelete} variant="contained" color="error" disabled={deleteServiceMutation.isLoading}>
              {deleteServiceMutation.isLoading ? (
                <><CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />{t("deleting") || "Deleting..."}</>
              ) : (
                t("delete")
              )}
            </Button>
          </>
        }
      >
        <Typography color="text.secondary">{t("confirm_delete_service")}</Typography>
      </CommonModal>
    </>
  );
};

export default ServiceManagement;
